import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { AuthImage } from "./AuthImage";
import { Heart, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

/**
 * MemoryCardDialog
 * Shown after an entry is marked as "experienced". User picks one of the
 * uploaded images as the hero photo for the Timeline / Memory Card view.
 *
 * Props:
 *   open, onOpenChange
 *   entry: { id, title, images, hero_image }
 *   onSelected: (updatedEntry) => void
 */
export const MemoryCardDialog = ({ open, onOpenChange, entry, onSelected }) => {
  const [selected, setSelected] = useState(entry?.hero_image || entry?.images?.[0] || null);
  const [saving, setSaving] = useState(false);

  if (!entry) return null;

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/entries/${entry.id}`, { hero_image: selected });
      toast.success("Erinnerungsfoto gewählt");
      onSelected && onSelected(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-alabaster border-border max-w-lg max-h-[92vh] overflow-y-auto" data-testid="memory-card-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-text_secondary mb-1">
            <Heart size={11} className="text-terracotta" /> Erlebt
          </div>
          <DialogTitle className="font-serif text-2xl text-ink">Eure Erinnerung</DialogTitle>
          <DialogDescription className="text-text_secondary">
            Wählt ein Foto, das eure gemeinsame Geschichte am besten festhält. Es erscheint in eurer Timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4">
          <div className="font-serif text-lg text-ink leading-tight mb-4">{entry.title}</div>
          <div className="grid grid-cols-3 gap-3">
            {(entry.images || []).map((fid) => {
              const isSelected = selected === fid;
              return (
                <button
                  key={fid}
                  type="button"
                  onClick={() => setSelected(fid)}
                  data-testid={`memory-image-${fid}`}
                  className={`relative aspect-square rounded-xl overflow-hidden bg-bone transition-transform ${
                    isSelected ? "ring-2 ring-offset-2 ring-ink scale-[1.02]" : "ring-1 ring-border hover:scale-[1.02]"
                  }`}
                >
                  <AuthImage fileId={fid} size="thumb" className="w-full h-full object-cover" alt="memory option" />
                  {isSelected && (
                    <div className="absolute inset-0 bg-ink/20 flex items-end justify-end p-2">
                      <div className="w-6 h-6 rounded-full bg-ink text-alabaster flex items-center justify-center">
                        <Check size={12} strokeWidth={2.4} />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="memory-skip-button">
            Später entscheiden
          </Button>
          <Button onClick={save} disabled={!selected || saving} className="rounded-full bg-ink text-alabaster hover:bg-ink/90" data-testid="memory-confirm-button">
            {saving ? "Speichern …" : "Auswählen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MemoryCardDialog;
